import React, { useState, useEffect } from 'react';
import {
  Button,
  Tab,
  TabList,
} from '@fluentui/react-components';
import { Dismiss24Regular, Save20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import ResourcesAccountComponent from '../ResourcesAccountComponent';
import GreetingComponent from '../GreetingComponent';
import HolidaysComponent from '../HolidaysComponent';
import SurveyComponent from '../Survey/SurveyComponent';
import RoutingEditPanel from './RoutingEditPanel';
import QueueSelectorComponent from '../../GenericComponent/QueueSelectorComponent';
import SearchResourceInGraph from '../../GenericComponent/SearchResourceInGraph';
import { Input, Label } from '@fluentui/react-components';
import './CSS/EditNodePanel.css';
/**
 * Panel d'édition pour les nœuds du diagramme
 */
const EditNodePanel = ({ 
  node, 
  isOpen, 
  onClose, 
  onSave, 
  callFlowData, 
  setCallFlowData, 
  ACTIVE_TENANT, 
  actionNodesData, 
  setActionNodesData,
  pendingActionNode,
  setPendingActionNode,
  setNodes,
  onDeleteNode
}) => {
  const { t } = useTranslation();
  const [localCallFlowData, setLocalCallFlowData] = useState(callFlowData);
  const [localActionNodeData, setLocalActionNodeData] = useState(null);
  const [panelWidth, setPanelWidth] = useState(700);
  const [isResizing, setIsResizing] = useState(false);
  const [activeTab, setActiveTab] = useState('message');
  
  // Déterminer le type de nœud
  const isRoutingNode = node?.id === 'routing-0' || node?.id?.startsWith('routing-');
  const isQueueNode = node?.id?.startsWith('queue-') || (node?.isNewActionNode && node?.id === 'queue');
  const isTransferUserNode = node?.id?.startsWith('transfer-user-') || (node?.isNewActionNode && node?.id === 'transfer-user');
  const isTransferPhoneNode = node?.id?.startsWith('transfer-pstn-') || (node?.isNewActionNode && node?.id === 'transfer-phone');
  const isDisconnectNode = node?.id === 'disconnect-node';
  const isActionNode = isQueueNode || isTransferUserNode || isTransferPhoneNode || isDisconnectNode;

  // Reset active tab when node changes
  useEffect(() => {
    if (node?.id === 'greeting') {
      setActiveTab('message');
    } else if (node?.id === 'holidays') {
      setActiveTab('vacances');
    } else if (node?.id === 'survey') {
      setActiveTab('enquete');
    } else if (isRoutingNode) {
      setActiveTab('routing');
    }
  }, [node?.id, isRoutingNode]);

  // Fonction pour supprimer le nœud actuel
  const handleDeleteNode = () => {
    if (onDeleteNode) {
      onDeleteNode(node.id);
    }
    onClose();
  };

  // Gestion du redimensionnement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 350 && newWidth <= 800) {
        setPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Initialiser localCallFlowData à l'ouverture du panel ou au changement de callFlowData
  useEffect(() => {
    if (callFlowData) {
      const updatedData = { ...callFlowData };
      
      // Initialiser greeting si le nœud est 'greeting' et que greeting n'existe pas
      if (node?.id === 'greeting' && !updatedData.greeting) {
        updatedData.greeting = {
          enabled: true,
          messages: []
        };
      }
      
      // Initialiser survey si le nœud est 'survey' et que survey n'existe pas
      if (node?.id === 'survey' && !updatedData.survey) {
        updatedData.survey = {
          enabled: true,
          questions: {},
          timeout: "2",
          goodByeMessage: {
            type: "Text2speech",
            language: updatedData.language || "en-US",
            text: "Quel est le taux de satisfaction ?",
            audioFileUrl: ""
          }
        };
      }
      
      // Initialiser deskOpen complètement pour holidays et open-hours
      if ((node?.id === 'holidays' || node?.id === 'open-hours') && !updatedData.deskOpen) {
        updatedData.deskOpen = {
          holidays: {
            weekendDays: [],
            daysOff: [],
            audio: {},
            holidaysAction: { type: 'disconnect' }
          },
          openHours: {},
          audio: {}
        };
      }
      
      // S'assurer que holidays existe si on est sur le nœud holidays
      if (node?.id === 'holidays' && !updatedData.deskOpen?.holidays) {
        updatedData.deskOpen = updatedData.deskOpen || {};
        updatedData.deskOpen.holidays = {
          weekendDays: [],
          daysOff: [],
          audio: {},
          holidaysAction: { type: 'disconnect' }
        };
      }
      
      // S'assurer que openHours existe si on est sur le nœud open-hours
      if (node?.id === 'open-hours' && !updatedData.deskOpen?.openHours) {
        updatedData.deskOpen = updatedData.deskOpen || {};
        updatedData.deskOpen.openHours = {};
      }
      
      setLocalCallFlowData(updatedData);
    }
  }, [callFlowData, node?.id]);

  // Initialiser les données du nœud d'action
  useEffect(() => {
    if (isActionNode && node?.id && actionNodesData) {
      setLocalActionNodeData(actionNodesData[node.id] || {});
    }
  }, [isActionNode, node?.id, actionNodesData]);
  // Logger les changements de localCallFlowData
  useEffect(() => {
  }, [localCallFlowData]);
  const handleSave = () => {
    let finalCallFlowData = { ...localCallFlowData };
    
    // Si c'est un routing, renommer la clé avec le displayName
    if (node?.id?.startsWith('routing-')) {
      const oldRoutingKey = node.id.replace('routing-', '');
      const routing = finalCallFlowData.routing?.[oldRoutingKey];
      
      // Protection : le routing "0" ne doit JAMAIS être renommé
      if (oldRoutingKey === '0') {
        // On met à jour seulement le displayName/routingName dans l'objet, pas la clé
        if (routing) {
          finalCallFlowData.routing['0'] = {
            ...routing,
            routingName: '0' // Force routingName à rester "0"
          };
        }
      } else if (routing) {
        // Ne pas renommer les routings de type terminal (transfer, queue, disconnect)
        // car ils ont des clés générées automatiquement et ne sont pas référencés par routingName
        const isTerminalRouting = ['transfer', 'queue', 'disconnect'].includes(routing.type);
        
        if (isTerminalRouting) {
          // Juste mettre à jour les champs sans changer la clé
          finalCallFlowData.routing[oldRoutingKey] = { ...routing };
        } else {
          let newName = null;
          
          // Obtenir le nouveau nom selon le type
          if (routing.type === 'ivr') {
            newName = routing.displayName;
          } else if (routing.type === 'prompt') {
            newName = routing.promptDisplayName;
          } else {
            // Pour les autres types (check_variable, time, regexp, etc.)
            newName = routing.displayName;
          }
          
          // Si un nom est défini et différent de la clé actuelle
          if (newName && newName.trim() && newName !== oldRoutingKey) {
            const newRoutingKey = newName.trim();
            
            // Vérifier que la nouvelle clé n'existe pas déjà
            if (!finalCallFlowData.routing[newRoutingKey] || newRoutingKey === oldRoutingKey) {
              
              // Créer un nouveau routing object avec la nouvelle clé
              const { [oldRoutingKey]: oldRouting, ...otherRoutings } = finalCallFlowData.routing;
              
              finalCallFlowData.routing = {
                ...otherRoutings,
                [newRoutingKey]: {
                  ...oldRouting,
                  routingName: newRoutingKey
                }
              };
              
              // Mettre à jour toutes les références à l'ancien routing dans les autres routings
              Object.keys(finalCallFlowData.routing).forEach(key => {
                const r = finalCallFlowData.routing[key];
                
                // Mettre à jour dans les options IVR
                if (r.options) {
                  Object.keys(r.options).forEach(optKey => {
                    if (r.options[optKey]?.type === 'routing' && r.options[optKey]?.routingName === oldRoutingKey) {
                      r.options[optKey].routingName = newRoutingKey;
                    }
                  });
                }
              
              // Mettre à jour dans fallbackAction
              if (r.fallbackAction?.type === 'routing' && r.fallbackAction?.routingName === oldRoutingKey) {
                r.fallbackAction.routingName = newRoutingKey;
              }
              
              // Mettre à jour dans next (Prompt)
              if (r.next?.type === 'routing' && r.next?.routingName === oldRoutingKey) {
                r.next.routingName = newRoutingKey;
              }
              
              // Mettre à jour dans onMatched/onUnMatched (check_variable)
              if (r.onMatched?.type === 'routing' && r.onMatched?.routingName === oldRoutingKey) {
                r.onMatched.routingName = newRoutingKey;
              }
              if (r.onUnMatched?.type === 'routing' && r.onUnMatched?.routingName === oldRoutingKey) {
                r.onUnMatched.routingName = newRoutingKey;
              }
            });
            
          }
        }
      }
    }
  }
    
    // 1. Mettre à jour les données du CallFlow global
    if (setCallFlowData) {
      setCallFlowData(finalCallFlowData);
    }
    // 2. Notifier le parent pour mettre à jour le nœud dans le diagramme
    if (onSave && node) {
      onSave(node.id, finalCallFlowData);
    }
    // 3. Fermer le panel
    onClose();
  };
  if (!isOpen) {
    return null;
  }
  if (!node) {
    return null;
  }
  
  // Vérifier que localCallFlowData est initialisé
  if (!localCallFlowData) {
    return null;
  }
  
  
  // Wrapper pour logger les changements
  const handleLocalDataChange = (newDataOrUpdater) => {
    setLocalCallFlowData(newDataOrUpdater);
  };
  // Rendu spécifique pour Resource Account
  const renderResourceAccountForm = () => (
    <div>
      <ResourcesAccountComponent
        callFlowData={localCallFlowData}
        setCallFlowData={handleLocalDataChange}
        isEditMode={!!callFlowData?.id}
      />
    </div>
  );
  // Rendu spécifique pour Greeting
  const renderGreetingForm = () => {
    // Créer des permissions par défaut pour permettre la modification
    const defaultPermissions = {
      greeting: {
        messages: {
          canRead: true,
          canCreate: true,
          canUpdate: true,
          canDelete: true
        }
      }
    };
    
    return (
      <div>
        <GreetingComponent
          callFlowData={localCallFlowData}
          setCallFlowData={handleLocalDataChange}
          ACTIVE_TENANT={ACTIVE_TENANT}
          permissions={localCallFlowData?.permissions || defaultPermissions}
          isEditMode={true}
        />
      </div>
    );
  };

  // Rendu spécifique pour Holidays
  const renderHolidaysForm = () => {
    // Créer des permissions par défaut pour permettre la modification
    const defaultPermissions = {
      holidays: {
        holidayActions: {
          canRead: true,
          canCreate: true,
          canUpdate: true,
          canDelete: true
        },
        openHour: {
          canRead: true,
          canCreate: true,
          canUpdate: true,
          canDelete: true
        }
      }
    };
    
    return (
      <div>
        <HolidaysComponent
          callFlowData={localCallFlowData}
          setCallFlowData={handleLocalDataChange}
          ACTIVE_TENANT={ACTIVE_TENANT}
          permissions={localCallFlowData?.permissions || defaultPermissions}
          isEditMode={true}
        />
      </div>
    );
  };

  // Rendu spécifique pour Open Hours (utilise HolidaysComponent qui contient aussi Open Hours)
  const renderOpenHoursForm = () => {
    // Créer des permissions par défaut pour permettre la modification
    const defaultPermissions = {
      holidays: {
        holidayActions: {
          canRead: true,
          canCreate: true,
          canUpdate: true,
          canDelete: true
        },
        openHour: {
          canRead: true,
          canCreate: true,
          canUpdate: true,
          canDelete: true
        }
      }
    };
    
    return (
      <div>
        <HolidaysComponent
          callFlowData={localCallFlowData}
          setCallFlowData={handleLocalDataChange}
          ACTIVE_TENANT={ACTIVE_TENANT}
          permissions={localCallFlowData?.permissions || defaultPermissions}
          isEditMode={true}
          defaultSection="openHours"
        />
      </div>
    );
  };

  // Rendu spécifique pour Survey
  const renderSurveyForm = () => {
    const defaultPermissions = {
      survey: {
        canRead: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true
      }
    };
    
    return (
      <div>
        <SurveyComponent
          callFlowData={localCallFlowData}
          setCallFlowData={handleLocalDataChange}
          ACTIVE_TENANT={ACTIVE_TENANT}
          permissions={localCallFlowData?.permissions || defaultPermissions}
          isEditMode={true}
        />
      </div>
    );
  };

  // Rendu spécifique pour Routing
  const renderRoutingForm = () => {
    // Extraire le routingKey de l'ID du nœud (ex: "routing-0" -> "0", "routing-123" -> "123")
    const routingKey = node.id?.startsWith('routing-') ? node.id.replace('routing-', '') : '0';
    
    return (
      <div style={{ height: '100%' }}>
        <RoutingEditPanel
          callFlowData={localCallFlowData}
          setCallFlowData={handleLocalDataChange}
          selectedRouting={routingKey}
          ACTIVE_TENANT={ACTIVE_TENANT}
          onSave={onSave}
        />
      </div>
    );
  };
  
  // Pour les nœuds d'action (queue, transfer, disconnect), afficher un formulaire éditable
  const renderActionNodeInfo = () => {
    
    if (isDisconnectNode) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>📴 Disconnect</h2>
          <p style={{ color: '#666', marginTop: '10px' }}>
            Ce nœud termine l'appel. Aucune configuration nécessaire.
          </p>
        </div>
      );
    }

    const handleSaveActionNode = () => {
      if (!localActionNodeData) {
        console.warn('⚠️ No data to save');
        return;
      }
      // Si c'est un nouveau nœud d'action (drop), créer le nœud visuel
      if (pendingActionNode && setNodes) {
        const nodeId = pendingActionNode.id;
        const position = pendingActionNode.position;
        
        // Sauvegarder les données dans actionNodesData
        setActionNodesData(prev => ({
          ...prev,
          [nodeId]: localActionNodeData
        }));
        
        // Créer le nœud visuel
        let newNode = null;
        let borderColor = '#10b981';
        let title = 'Queue';
        let displayName = localActionNodeData.displayName || 'Not configured';
        
        if (pendingActionNode.type === 'transfer-user') {
          borderColor = '#92400e';
          title = 'Transfer User';
          displayName = localActionNodeData.displayName || 'Not configured';
        } else if (pendingActionNode.type === 'transfer-phone') {
          borderColor = '#000000';
          title = '☎️ Transfer Phone';
          displayName = localActionNodeData.phoneNumber || 'Not configured';
        }
        
        newNode = {
          id: nodeId,
          type: 'default',
          position,
          deletable: true,
          data: {
            label: (
              <div className={`node-${pendingActionNode.type}`}>
                <div className="node-title">{title}</div>
                <div className="node-separator"></div>
                <div className="node-info">
                  <div className="node-info-label">{displayName}</div>
                </div>
              </div>
            ),
          },
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            border: `4px solid ${borderColor}`,
            borderRadius: '10px',
            padding: '15px',
            minWidth: '250px',
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
          },
        };
        
        setNodes((nds) => nds.concat(newNode));
        setPendingActionNode(null);
        onClose();
      } else if (setActionNodesData && node?.id) {
        // Mise à jour d'un nœud existant
        setActionNodesData(prev => ({
          ...prev,
          [node.id]: localActionNodeData
        }));
        onClose();
      }
    };
    
    return (
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {isQueueNode && (
          <>
            <h2>Queue</h2>
            <div>
              <Label>Sélectionner une queue</Label>
              <QueueSelectorComponent 
                displayName={localActionNodeData?.displayName || ''} 
                getData={(data) => {
                  setLocalActionNodeData({
                    id: data.id,
                    displayName: data.displayName,
                    type: 'queue'
                  });
                }} 
              />
            </div>
            {localActionNodeData?.displayName && (
              <div style={{ padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <strong>Queue sélectionnée:</strong> {localActionNodeData.displayName}
              </div>
            )}
          </>
        )}
        
        {isTransferUserNode && (
          <>
            <h2>Transfer User</h2>
            <div>
              <Label>Rechercher un utilisateur</Label>
              <SearchResourceInGraph
                displayName={localActionNodeData?.displayName || ''}
                onSelectUser={(user) => {
                  setLocalActionNodeData({
                    id: user.id || user.localAccountId,
                    displayName: user.displayName,
                    type: 'user'
                  });
                }}
              />
            </div>
            {localActionNodeData?.displayName && (
              <div style={{ padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <strong>Utilisateur sélectionné:</strong> {localActionNodeData.displayName}
              </div>
            )}
          </>
        )}
        
        {isTransferPhoneNode && (
          <>
            <h2>☎️ Transfer Phone</h2>
            <div>
              <Label>Numéro de téléphone</Label>
              <Input
                placeholder="Entrez le numéro de téléphone"
                value={localActionNodeData?.phoneNumber || ''}
                onChange={(_, data) => {
                  setLocalActionNodeData({
                    phoneNumber: data.value,
                    type: 'phone'
                  });
                }}
              />
            </div>
            {localActionNodeData?.phoneNumber && (
              <div style={{ padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <strong>Numéro:</strong> {localActionNodeData.phoneNumber}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <Button appearance="primary" onClick={handleSaveActionNode} icon={<Save20Regular />}>
            Sauvegarder
          </Button>
          <Button appearance="secondary" onClick={onClose}>
            Annuler
          </Button>
        </div>
      </div>
    );
  };

  if (!node) {
    return null;
  }

  return (
    <div 
      className={`edit-node-panel ${isOpen ? 'open' : ''}`}
      style={{ width: `${panelWidth}px`, right: isOpen ? 0 : `-${panelWidth}px` }}
    >
      <div 
        className="resize-handle"
        onMouseDown={() => setIsResizing(true)}
      />
      <div className="panel-header">
        <h3>
          {node.id === 'resource-account' && 'Compte de ressources'}
          {node.id === 'greeting' && 'Message d\'accueil'}
          {node.id === 'holidays' && 'Vacances'}
          {node.id === 'open-hours' && 'Open Hours'}
          {node.id === 'survey' && 'Enquête'}
          {isRoutingNode && 'Routage'}
          {isQueueNode && 'Queue'}
          {node.id?.startsWith('transfer-user-') && 'Transfert Utilisateur'}
          {node.id?.startsWith('transfer-pstn-') && 'Transfert Téléphone'}
          {isDisconnectNode && 'Raccrocher'}
        </h3>
        <div className="panel-header-actions">
          {!isActionNode && (
            <Button 
              appearance="primary" 
              onClick={handleSave}
              icon={<Save20Regular />}
              style={{ 
                backgroundColor: '#5b5fc7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                fontWeight: 500
              }}
            >
              {t('SAVE')}
            </Button>
          )}
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClose}
            className="close-btn"
          />
        </div>
      </div>
      
      <div className="panel-content">
        {node.id === 'resource-account' && renderResourceAccountForm()}
        {node.id === 'greeting' && renderGreetingForm()}
        {node.id === 'holidays' && renderHolidaysForm()}
        {node.id === 'open-hours' && renderOpenHoursForm()}
        {node.id === 'survey' && renderSurveyForm()}
        {isRoutingNode && renderRoutingForm()}
        {isActionNode && renderActionNodeInfo()}
        {!node.id && <div style={{padding: '20px'}}>Aucun nœud sélectionné</div>}
      </div>
    </div>
  );
};
export default EditNodePanel;
